import { Trans } from "@lingui/react/macro";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { useMemo, useRef } from "react";

import { transpose } from "../../../utils/utils";
import { Spinner } from "../spinner";
import styles from "../table.module.css";
import { DualScrollbarContainer } from "./dual-scrollbar-container";
import { useStickyColumns } from "./sticky-columns";

export interface Column<T> {
  header1: React.ReactNode;
  header2?: React.ReactNode;
  header3?: React.ReactNode;
  cell: (row: T, index: number) => React.ReactNode;
  sticky?: boolean;
  align?: "left" | "center" | "right";
}

const ALIGN_CLASS = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

export const SimpleTable = observer(function SimpleTable<T>({
  rows,
  columns: propsColumns,
  itemKeyField,
  long,
  verticalAlign,
  foot,
  loading,
  countNoun,
  rowAttrs,
  cellAttrs,
}: {
  rows: T[];
  columns: Array<Column<T> | false | null | undefined>;
  itemKeyField?: keyof T;
  long?: boolean;
  verticalAlign?: "top";
  foot?: React.ReactNode;
  loading?: boolean;
  countNoun?: { singular: string; plural: string };
  rowAttrs?: (props: {
    row: T;
    index: number;
  }) => React.HTMLAttributes<HTMLTableRowElement>;
  cellAttrs?: (props: {
    row: T;
    col: Column<T>;
    rowIndex: number;
    colIndex: number;
  }) => React.HTMLAttributes<HTMLTableCellElement>;
}) {
  const tableRef = useRef<HTMLTableElement>(null);

  const columns = useMemo(
    () => propsColumns.filter((col): col is Column<T> => !!col),
    [propsColumns],
  );

  const headers = useMemo(() => {
    const headers: Array<Array<React.ReactNode>> = [];

    for (const col of columns) {
      const header: Array<React.ReactNode> = [];

      header.push(col.header1);

      if (col.header2) {
        header.push(col.header2);
      }

      if (col.header3) {
        if (header.length === 1) {
          header.push(null);
        }
        header.push(col.header3);
      }

      headers.push(header);
    }

    return transpose(headers);
  }, [columns]);

  useStickyColumns(tableRef);

  return (
    <div className="relative w-full">
      {countNoun && (
        <div className="flex justify-end">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            <strong className="text-foreground">{rows.length}</strong>{" "}
            {rows.length === 1 ? countNoun.singular : countNoun.plural}
          </span>
        </div>
      )}
      <DualScrollbarContainer className={styles.tableContainer}>
        <table
          className={clsx(
            styles.table,
            long && styles.longTable,
            verticalAlign === "top" && styles.verticalAlignTop,
          )}
          ref={tableRef}
        >
          <thead>
            {headers.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((header, colIndex) => (
                  <th
                    key={colIndex}
                    className={
                      ALIGN_CLASS[columns[colIndex]?.align ?? "center"]
                    }
                    data-sticky={columns[colIndex]?.sticky}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody inert={loading}>
            {rows.length === 0 ? (
              <tr style={{ pointerEvents: "none" }}>
                <td
                  colSpan={columns.length}
                  style={{
                    height: "120px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  {!loading && <Trans>No data</Trans>}
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => (
                <tr
                  key={itemKeyField ? String(row[itemKeyField]) : rowIndex}
                  {...rowAttrs?.({ row, index: rowIndex })}
                >
                  {columns.map((col, colIndex) => (
                    <td
                      key={colIndex}
                      className={ALIGN_CLASS[col.align ?? "center"]}
                      data-sticky={col.sticky}
                      {...cellAttrs?.({
                        row,
                        col,
                        rowIndex,
                        colIndex,
                      })}
                    >
                      {col.cell(row, rowIndex)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>

          {foot && <tfoot>{foot}</tfoot>}
        </table>
      </DualScrollbarContainer>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
          <Spinner size={24} />
        </div>
      )}
    </div>
  );
});
